import { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toJS } from 'mobx';
import { observer, useLocalObservable } from 'mobx-react-lite';
import { Button } from 'components/buttons/button';
import { ButtonType } from 'components/buttons/button-types';
import { Card } from 'components/cards/card';
import { BasicLoader } from 'components/loaders/basic-loader';
import { AppContext } from 'core/context/app.context';
import { AnalyticsEventJourneyKey, AnalyticsJourneyKey } from 'core/enums/analytics-journey-key';
import { FeatureFlag } from 'core/enums/feature-flag';
import { ActionOverlays } from 'core/enums/overlay/action-overlays';
import { PaymentAttribution } from 'core/enums/payment-type';
import { usePlatform } from 'core/hooks/platform';
import PageRoute from 'core/page-route';
import { AnalyticsEventStore } from 'core/stores/analytics-events.store';
import { AnalyticsStructureStore } from 'core/stores/analytics-structure.store';
import { PaymentStatus } from 'core/stores/payment/payment.store';
import { useMakePayment } from 'payment/hooks/use-make-payment';
import { Util } from 'utils/common.utils';
import { AmountToPayLayout, AmountToPayStore } from './amount-to-pay.store';
import { AmountToPayCompactCard } from './layouts/compact-card';
import { AmountToPayStandardCard } from './layouts/standard-card';
import { useGotoRecurringPayment } from '../recurring-payment/lib';
import styles from './amount-to-pay.module.scss';

export const AmountToPay = observer<{
  layout: AmountToPayLayout;
  analyticsEntryPoint?: string;
  customJourneyName?: ActionOverlays | FeatureFlag;
  customAreaName?: string;
  customComponentName?: string;
}>(({ layout = AmountToPayLayout.StandardCard, analyticsEntryPoint, customJourneyName, customAreaName, customComponentName }) => {
  const { t } = useTranslation();
  const gotoRecurringPayment = useGotoRecurringPayment();
  const { customerNumber, countryName, paymentStore, currencyStore, featureStore, isLesotho } = useContext(AppContext);

  const { payAmount } = useMakePayment();
  const { platformKey } = usePlatform();

  const amountToPayStore = useLocalObservable(() => new AmountToPayStore());
  const { isLoading, formattedValue, editedValue, dueDate, cardBackground, disablePayButton, account } =
    amountToPayStore;

  const isCompact = layout === AmountToPayLayout.CompactCard;
  const isButton = layout === AmountToPayLayout.Button;

  const isPaymentBlocked = featureStore.hasViewPermission(FeatureFlag.BlockLesothoPayments) && isLesotho;

  useEffect(() => {
    const fetchMarketMinMaxValidations = featureStore.hasViewPermission(FeatureFlag.UseMarketMinMaxValidations);

    amountToPayStore.initialize(customerNumber, countryName, currencyStore, fetchMarketMinMaxValidations).then((_) =>
      AnalyticsStructureStore.updateProperties({
        paymentDueAmount: amountToPayStore.formattedValue,
        amountToPay: amountToPayStore.editedValue || amountToPayStore.formattedValue,
        paymentDueDate: amountToPayStore.dueDate.toDateString(),
      })
    );

    amountToPayStore.setCardType(layout, platformKey);
  }, [customerNumber, amountToPayStore, layout, countryName, currencyStore]);

  useEffect(() => {
    if (paymentStore.status === PaymentStatus.Success) {
      amountToPayStore.fetchAccountDetails(customerNumber, countryName);
    }
  }, [paymentStore.status, customerNumber, countryName]);

  const setupRecurringPayment = () => gotoRecurringPayment();

  const manageRecurringPayment = () => gotoRecurringPayment();

  const makePayment = () => {
    const amount = editedValue
      ? currencyStore.getAsMainCurrency(`${editedValue}`.replace(',', ''))
      : amountToPayStore?.amount?.value;

    const settledAccount = amount && amount === account?.dueAmount?.value;
    const timelyPayment = new Date().getTime() <= dueDate.getTime();

    payAmount({
      returnLocation: Util.CurrentLocation(),
      amountToPay: `${amount}`,
      account: toJS(account),
      paymentMetaData: {
        paymentAttribution: PaymentAttribution.PayAccount,
        journeyName: customJourneyName,
        eventData: {
          amount: `${amount}`,
          paymentMethod: `${account.methodOfPayment}`,
          settledAccount,
          timelyPayment,
        },
        analyticsTagging: {
          areaName: customAreaName || 'Amount to pay',
          componentName: customComponentName || 'amountToPayButton',
          entryPoint: analyticsEntryPoint,
        },
      },
    });

    AnalyticsEventStore.addEvent(AnalyticsJourneyKey.AccountDashboard, AnalyticsEventJourneyKey.BeginCheckout, {
      ...amountToPayStore,
      analyticsEntryPoint,
      products: [],
    });
  };

  if (isButton) {
    if (isPaymentBlocked) {
      return <div className="text-small">{t('common.paymentBlocked')}</div>;
    }

    return (
      <Button
        meta={{
          type: ButtonType.Primary,
          title: t('buttons.payNow'),
          name: 'payNowButton',
          areaName: customAreaName || 'Self Service Accounts - My Bill Explained',
          actionName: 'payNow',
          componentName: 'amountToPay',
          disabled: disablePayButton,
          entryPoint: analyticsEntryPoint,
          paymentDueAmount: formattedValue,
        }}
        classes={['align-self-center', 'full-width', `${styles.wideButton}`]}
        onClick={makePayment}
        analyticsComponentName="amountToPayButton"
      ></Button>
    );
  }

  const getAmountToPayCard = () => {
    const props = {
      amountToPayStore,
      isPaymentBlocked,
      onPayNow: makePayment,
      onManageRecurringPayment: manageRecurringPayment,
      onSetupRecurringPayment: setupRecurringPayment,
      analyticsEntryPoint
    };

    return isCompact
      ? <AmountToPayCompactCard {...props} styles={styles} />
      : <AmountToPayStandardCard {...props} />
    ;
  };

  return (
    <Card backgroundType={cardBackground} noMargin={true}>
      <BasicLoader isLoading={isLoading}>{getAmountToPayCard()}</BasicLoader>
    </Card>
  );
});